import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate L6-10 del Warlock (manuale V1, rr. 6100-6177).
 *
 * 14 mosse di tipo `advanced_6_10`. Pattern stress-testati:
 *
 * - **Catene di prerequisiti L2-5 → L6-10** (5 catene):
 *   - *Braccio Destro* → *Un Giusto Sovrano* (FI-8 stub)
 *   - *Servitore Competente* → *Maestria del Servitore* (FI-8 stub)
 *   - *Deflagrazione Potente* → *Deflagrazione Dirompente*
 *   - *Lama Ultraterrena* → *Stormbringer*
 *   - *Tomo del Sapere* → *Rivelazioni Proibite*
 *   - *Setta* → *Loggia* (organizzazione, non gregario)
 *
 * - **`prerequisites.anyOf` cross-class verso Paladino**: 4 mosse hanno
 *   «Richiede: *Contratto* o *Ordine Sovrannaturale*» (manuale rr. 6149, 6161, 6175).
 *
 * - **Mossa multiclasse** (FI-17): *... Sono Infinite* sceglie da Paladino/Mago/Guerriero.
 *   Description-only.
 *
 * - **FI-8 stub description-only** (D6): *Un Giusto Sovrano* (secondo gregario), *Maestria
 *   del Servitore*. Refactor unificato Ranghi 1-3 nella PR FI-8 post-12 classi.
 *
 * - ***Aspetto del Patrono***: concede 2 mosse runtime accordate dal GM (analogo concettuale
 *   a *Uno con lo Spirito* dello Sciamano). Description-only sull'effetto.
 */

// Helper per il prereq cross-class strutturato. Ritorna un nuovo oggetto
// a ogni invocazione (non `as const`) per evitare il problema readonly→mutable
// di Zod su `PrerequisiteSchema`.
function contrattoOrOrdineSovrannaturale(): { anyOf: string[] } {
  return { anyOf: ['warlock.contratto', 'paladino.ordine-sovrannaturale'] };
}

const sonoInfinite: Move = {
  id: 'warlock.sono-infinite',
  name: '... Sono Infinite',
  type: 'advanced_6_10',
  classId: 'warlock',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Scegli una mossa non multiclasse dalla lista del Paladino, del Mago o del Guerriero.',
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool ristretto a 3 classi.
  multiclass: {
    picks: { kind: 'fixed-classes', classes: ['paladino', 'mago', 'guerriero'] },
  },
};

const postille: Move = {
  id: 'warlock.postille',
  name: 'Postille',
  type: 'advanced_6_10',
  classId: 'warlock',
  trigger: 'Quando prendi questa mossa al level-up',
  description: 'Scegli un altro beneficio dal tuo *Contratto*: lo ottieni.',
  tags: [],
  prerequisites: ['warlock.contratto'],
  replaces: [],
};

const unGiustoSovrano: Move = {
  id: 'warlock.un-giusto-sovrano',
  name: 'Un Giusto Sovrano',
  type: 'advanced_6_10',
  classId: 'warlock',
  trigger: 'Quando prendi questa mossa al level-up (configurazione del 2° servitore)',
  description:
    'Puoi evocare un secondo emissario del patrono. Configuralo come hai fatto col primo (nome, descrizione, modificatore +2, Abilità primaria R1, capacità utile). Ogni volta che il sole tramonta devi decidere quale dei due servitori appare al tuo fianco.\n\nLa logica «quale è attivo oggi» è gestita al tavolo (Fase F: modalità giocabile).',
  tags: [],
  prerequisites: ['warlock.braccio-destro'],
  replaces: [],
  // D8b — FI-8e: 2° emissario, template duplicato `warlock-un-giusto-sovrano`
  // (Q3=β). I narrativeField sono distinti dal Braccio Destro (`-2` suffix).
  followerTemplateId: 'warlock-un-giusto-sovrano',
  choiceGroups: [
    {
      id: 'modificatore',
      label: 'Modificatore a +2 (2° emissario)',
      description: 'Indipendente dal 1° Braccio Destro.',
      pickCount: 1,
      options: [
        { id: 'forza', label: 'Forza' },
        { id: 'destrezza', label: 'Destrezza' },
        { id: 'costituzione', label: 'Costituzione' },
        { id: 'intelligenza', label: 'Intelligenza' },
        { id: 'saggezza', label: 'Saggezza' },
        { id: 'carisma', label: 'Carisma' },
      ],
    },
  ],
  narrativeFields: [
    {
      id: 'nome-emissario-2',
      label: 'Nome del secondo emissario',
      placeholder: 'es. Mortafrancata, Il Vecchio Luminoso, Cirian…',
      required: true,
      multiline: false,
    },
    {
      id: 'descrizione-emissario-2',
      label: 'Descrizione del secondo emissario',
      placeholder:
        'Aspetto, natura, comportamento. Differenze rispetto al primo emissario, se rilevanti.',
      required: false,
      multiline: true,
    },
    {
      id: 'abilita-primaria-2',
      label: 'Abilità primaria del 2° emissario (Rango 1)',
      placeholder: "Scegli l'Abilità (può essere diversa dal primo).",
      required: true,
      multiline: false,
      oneOf: [
        'adepto',
        'animista',
        'battitore',
        'combattente',
        'guardia-del-corpo',
        'menestrello',
        'sacerdote',
        'scassinatore',
        'scout',
      ],
    },
    {
      id: 'capacita-utile-label-2',
      label: 'Capacità utile del 2° emissario — titolo',
      placeholder: 'es. Sguardo paralizzante, Forma vapore, Maestro delle ombre…',
      required: true,
      multiline: false,
    },
    {
      id: 'capacita-utile-desc-2',
      label: 'Capacità utile del 2° emissario — descrizione',
      placeholder: 'Effetto narrativo o meccanico, concordato con il GM.',
      required: false,
      multiline: true,
    },
  ],
};

const maestriaDelServitore: Move = {
  id: 'warlock.maestria-del-servitore',
  name: 'Maestria del Servitore',
  type: 'advanced_6_10',
  classId: 'warlock',
  trigger: 'Quando prendi questa mossa al level-up (upgrade del Braccio Destro)',
  description:
    "Il livello da gregario del servitore aumenta a 3, così come il Rango nell'Abilità primaria. La Caratteristica che aveva a +2 sale a +3. Concorda con il GM una nuova capacità utile, o porta a Rango 2 una delle sue Abilità secondarie.",
  tags: [],
  prerequisites: ['warlock.servitore-competente'],
  replaces: [],
  // D8c — FI-41: upgrade with choice del Braccio Destro Warlock.
  // Il template dichiara `levelOverride: 3` + `modifierBonusOverride: amount 3`
  // + `primarySkillRankOverride: 3` + `peculiaritiesFromChoiceGroup` che
  // mappa le 2 opzioni qui dichiarate.
  choiceGroups: [
    {
      id: 'bonus',
      label: 'Bonus aggiuntivo al servitore (oltre level 3 / primaria R3 / mod +3)',
      description:
        'Scegli come ulteriormente potenziare il servitore. Gli effetti statici (livello, primaria, mod) sono sempre applicati.',
      pickCount: 1,
      options: [
        { id: 'nuova-capacita', label: 'Una nuova capacità utile' },
        { id: 'sec-r2', label: 'Porta a Rango 2 una delle Abilità secondarie esistenti' },
      ],
    },
  ],
  narrativeFields: [
    {
      id: 'maestria-cap-label',
      label: 'Nuova capacità utile — titolo',
      placeholder: 'es. Profezia, Comunicazione mentale, Volo planato…',
      required: true,
      multiline: false,
      requiresChoice: { groupId: 'bonus', optionId: 'nuova-capacita' },
    },
    {
      id: 'maestria-cap-desc',
      label: 'Nuova capacità utile — descrizione',
      placeholder: 'Effetto narrativo o meccanico, concordato con il GM.',
      required: false,
      multiline: true,
      requiresChoice: { groupId: 'bonus', optionId: 'nuova-capacita' },
    },
    {
      id: 'maestria-sec-quale',
      label: 'Quale Abilità secondaria portare a Rango 2',
      placeholder: "Scegli l'Abilità secondaria già esistente.",
      required: true,
      multiline: false,
      oneOf: [
        'adepto',
        'animista',
        'battitore',
        'combattente',
        'guardia-del-corpo',
        'menestrello',
        'sacerdote',
        'scassinatore',
        'scout',
      ],
      requiresChoice: { groupId: 'bonus', optionId: 'sec-r2' },
    },
  ],
};

const deflagrazioneDirompente: Move = {
  id: 'warlock.deflagrazione-dirompente',
  name: 'Deflagrazione Dirompente',
  type: 'advanced_6_10',
  classId: 'warlock',
  trigger: 'Sempre (effetto continuo su *Deflagrazione Occulta*)',
  description:
    "Aggiungi anche queste etichette alla lista di *Deflagrazione Occulta*: *lontano*, *area* (colpisce tutti i nemici entro portata *lunga* dal bersaglio, ma infligge la metà dei danni, arrotondati per eccesso) e *brutale*. Inoltre, l'etichetta 2 *perforante* è sostituita da 3 *perforante* e l'etichetta +1d4 danni è sostituita da +1d6 danni. Infine, puoi scegliere 3 etichette *diverse* anziché 2.",
  tags: [],
  prerequisites: ['warlock.deflagrazione-potente'],
  replaces: [],
};

const stormbringer: Move = {
  id: 'warlock.stormbringer',
  name: 'Stormbringer',
  type: 'advanced_6_10',
  classId: 'warlock',
  trigger: 'Quando uccidi una creatura con la tua *Lama Ultraterrena*',
  description:
    "La sua anima, sempre che ne abbia una, viene intrappolata nell'arma. L'anima è consapevole della sua prigionia e di dove si trova, ma puoi comunque manipolarla *parlamentando*, usando incantesimi, o con altri effetti che siano ragionevolmente in grado di influenzare un'anima. Tutte le mosse effettuate contro le anime intrappolate ottengono +1 al tiro, ma non sperare di poterle trattenere in eterno: il tuo patrono, presto o tardi, le reclamerà.",
  tags: [],
  prerequisites: ['warlock.lama-ultraterrena'],
  replaces: [],
};

const rivelazioniProibite: Move = {
  id: 'warlock.rivelazioni-proibite',
  name: 'Rivelazioni Proibite',
  type: 'advanced_6_10',
  classId: 'warlock',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Il tuo patrono ti rivela oscuri segreti dimenticati nei meandri del tempo e dello spazio. Aggiungi al tuo Tomo un incantesimo di 5° livello tra quelli del Warlock, e altri due incantesimi di qualsiasi livello (ma al massimo uno solo di 9°) a tua scelta da qualunque lista di classe (inclusa quella del Warlock).',
  tags: [],
  prerequisites: ['warlock.tomo-del-sapere'],
  replaces: [],
  // FI-33 (D2): manuale V1 r. 6134. Due grant `on-take` complementari:
  //  1. 1 spell di livello 5 fisso dalla lista del Warlock
  //  2. 2 spell di qualsiasi livello da qualunque classe (Warlock incluso),
  //     con cap 1 spell di livello 9 (`perLevelCap: { '9': 1 }`)
  grantsSpells: [
    {
      trigger: 'on-take',
      count: 1,
      levelRule: { kind: 'fixed', level: 5 },
      source: { kind: 'own-class' },
    },
    {
      trigger: 'on-take',
      count: 2,
      levelRule: { kind: 'any-with-cap' },
      source: { kind: 'any-class' },
      perLevelCap: { '9': 1 },
    },
  ],
};

const loggia: Move = {
  id: 'warlock.loggia',
  name: 'Loggia',
  type: 'advanced_6_10',
  classId: 'warlock',
  trigger: 'Quando fai il *fine sessione* o quando usi la mossa *Setta* per ottenere prese',
  description:
    'Quando **fai il *fine sessione***, guadagni una presa di *Setta* in ognuno dei seguenti casi:\n\n- se rispondi «*sì*» alla domanda «*Hai mostrato al mondo il tuo potere o quello del tuo patrono?*» che ti verrà posta dal GM;\n- se hai lasciato il tuo *Braccio Destro* a gestire la *Setta* invece che portarlo con te.\n\nInoltre, quando **usi la mossa *Setta* per ottenere prese**, con 12+, ne ottieni 2 anziché 1 sola.',
  tags: [],
  prerequisites: ['warlock.setta'],
  replaces: [],
};

const animaReclamata: Move = {
  id: 'warlock.anima-reclamata',
  name: 'Anima Reclamata',
  type: 'advanced_6_10',
  classId: 'warlock',
  trigger: 'Quando esali il tuo *ultimo respiro*',
  description:
    'Il tuo patrono ha riscattato la tua anima anche dal Tristo Mietitore. Considera di aver ottenuto un risultato di 10+, senza bisogno di tirare i dadi. Tuttavia, il patrono pretenderà qualcosa in cambio per questa nuova opportunità (e non è detto che per te sia una cosa piacevole!).',
  tags: [],
  prerequisites: [contrattoOrOrdineSovrannaturale()],
  replaces: [],
};

const provvigione: Move = {
  id: 'warlock.provvigione',
  name: 'Provvigione',
  type: 'advanced_6_10',
  classId: 'warlock',
  trigger: 'Quando convinci qualcuno a stringere un patto col tuo patrono',
  description:
    "Tira+**CAR**. Con 7+, segna 1 PE (al massimo una volta a sessione). Con 10+, inoltre, ricevi una ricompensa. Con 6-, invece, l'accordo avrà conseguenze nefaste sulla storia, oppure il tuo patrono, insoddisfatto del patto appena concluso, te ne addosserà la colpa (a discrezione del GM).",
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Segna 1 PE + ricevi una ricompensa.' },
    on7to9: { text: 'Segna 1 PE.' },
    on6minus: { text: 'Conseguenze nefaste sulla storia o colpa addossata dal patrono.' },
  },
  tags: [],
  prerequisites: ['warlock.contratto'],
  replaces: [],
};

// Le 2 mosse runtime accordate dal GM sono description-only (analogo
// concettuale a *Uno con lo Spirito* dello Sciamano).
const aspettoDelPatrono: Move = {
  id: 'warlock.aspetto-del-patrono',
  name: 'Aspetto del Patrono',
  type: 'advanced_6_10',
  classId: 'warlock',
  trigger:
    'Alla presa: descrivi come il patrono si manifesta sul tuo aspetto + concorda 2 mosse con il GM. A invocazione: quando scegli di rivelare la tua vera natura',
  description:
    "La prima volta che usi questa mossa, descrivi come si manifesta l'essenza del tuo patrono sul tuo aspetto fisico quando lasci che il suo potere ti pervada completamente. Inoltre, concorda con il GM due mosse che rappresentino le capacità legate alla manifestazione. Queste mosse hanno sempre l'innesco «*Quando **spendi una presa di *Aspetto del Patrono***...*» e non richiedono alcun tiro per essere usate.\n\nQuando **scegli di rivelare la tua vera natura**, il tuo corpo cambia per adattarsi alle caratteristiche del tuo patrono. Tira+**COS**. Con 10+, ottieni 3 prese. Con un 7-9, ne ottieni 2. Con 6- hai contrariato il patrono, che ti punirà per questo. Puoi spendere 1 presa di *Mana* quando usi questa mossa per ottenere una presa in più di *Aspetto del Patrono*, anche in caso di fallimento. Al tramonto, o una volta esaurite le prese, torni alla tua forma normale.",
  roll: {
    stat: 'costituzione',
    on10plus: { text: '3 prese di *Aspetto del Patrono*.' },
    on7to9: { text: '2 prese di *Aspetto del Patrono*.' },
    on6minus: { text: 'Hai contrariato il patrono, che ti punirà.' },
  },
  tags: [],
  prerequisites: [contrattoOrOrdineSovrannaturale()],
  replaces: [],
};

const comunioneConLeOmbre: Move = {
  id: 'warlock.comunione-con-le-ombre',
  name: 'Comunione con le Ombre',
  type: 'advanced_6_10',
  classId: 'warlock',
  trigger: 'Quando sei completamente celato dalle tenebre',
  description:
    "Tira+**DES**. Con 10+, sei del tutto invisibile nell'oscurità. Con 7-9, sei comunque invisibile tra le ombre, ma lasci qualche segno della tua presenza: un tenue odore di zolfo, un velo di brina su ciò che tocchi, ecc.",
  roll: {
    stat: 'destrezza',
    on10plus: { text: "Del tutto invisibile nell'oscurità." },
    on7to9: { text: 'Invisibile tra le ombre, ma lasci segni minori.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const unaMisteriosaFiguraMascherata: Move = {
  id: 'warlock.una-misteriosa-figura-mascherata',
  name: 'Una Misteriosa Figura Mascherata',
  type: 'advanced_6_10',
  classId: 'warlock',
  trigger: 'Quando indossi la maschera ultraterrena',
  description:
    "Hai avuto accesso a una maschera ultraterrena. Quando la indossi, vieni scambiato per una persona differente, e la gente non è in grado di identificare nulla del tuo io mascherato che riconduca a te e viceversa. Il tuo io mascherato può avere un nome che hai pensato per lui o non averlo affatto. Se viene chiesto a qualcuno di descrivere il tuo io mascherato, l'unica descrizione che salterà fuori è: «*Indossava una maschera…*».",
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-18: setup-levelup. Descrizione maschera + nome io mascherato (opz).
  narrativeFields: [
    {
      id: 'descrizione-maschera',
      label: 'Descrivi la tua maschera',
      placeholder:
        'Forma, materiali ultraterreni, espressione, glifi, sensazione che dà a chi la vede…',
      required: true,
      multiline: true,
    },
    {
      id: 'nome-io-mascherato',
      label: 'Nome del tuo io mascherato (opzionale)',
      placeholder: 'es. Il Pellegrino Nero, La Voce dei Vicoli, Aragosth…',
      required: false,
      multiline: false,
    },
  ],
};

const manifestazioneDelRegno: Move = {
  id: 'warlock.manifestazione-del-regno',
  name: 'Manifestazione del Regno',
  type: 'advanced_6_10',
  classId: 'warlock',
  trigger: "Quando hai bisogno che l'ambiente attorno a te sia diverso da com'è",
  description:
    "Tira+**CAR**. Con 10+, una parte del piano di esistenza del patrono si manifesta in questo mondo: descrivi come si presenta ora l'ambiente che ti circonda. Con un 7-9, descrivi comunque come si modifica l'ambiente circostante, ma il GM ti dirà cosa, in realtà, non è cambiato. La manifestazione è instabile e non dura più di qualche minuto. Con questa mossa non puoi danneggiare direttamente qualcuno o qualcosa, come, ad esempio, facendo apparire un baratro sotto i piedi di un avversario.",
  roll: {
    stat: 'carisma',
    on10plus: { text: "Il piano del patrono si manifesta: descrivi l'ambiente." },
    on7to9: { text: 'Lo stesso, ma il GM dice cosa non è cambiato davvero.' },
  },
  tags: [],
  prerequisites: [contrattoOrOrdineSovrannaturale()],
  replaces: [],
};

export const warlockAdvanced6to10Moves: readonly Move[] = [
  sonoInfinite,
  postille,
  unGiustoSovrano,
  maestriaDelServitore,
  deflagrazioneDirompente,
  stormbringer,
  rivelazioniProibite,
  loggia,
  animaReclamata,
  provvigione,
  aspettoDelPatrono,
  comunioneConLeOmbre,
  unaMisteriosaFiguraMascherata,
  manifestazioneDelRegno,
] as const;
